import React, { useState, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Card, Avatar, Tooltip, Badge, Menu, Dropdown, Button, Drawer } from 'antd';
import { UserOutlined, SettingOutlined, LogoutOutlined, HomeOutlined, UserAddOutlined, ShopOutlined, TagOutlined, EnvironmentOutlined, ExclamationCircleOutlined, CrownOutlined, MenuOutlined } from '@ant-design/icons';
import { isAuthenticated } from 'services/auth';
import './UnifiedDashboard.css';

const { Meta } = Card;

const UnifiedDashboard = ({ children }) => {
  const { user } = isAuthenticated();
  const history = useHistory();
  const [collapsed, setCollapsed] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detectar se é mobile
  useEffect(() => {
    const checkIsMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      // No mobile, manter drawer fechado por padrão
      if (mobile) {
        setDrawerVisible(false);
      }
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  const logout = () => {
    window.localStorage.removeItem('buynsell');
    history.push('/login');
    window.location.reload();
  };

  // Menu items baseado na role do usuário
  const getMenuItems = () => {
    if (!user) return [];

    const createProductBtn = {
      key: 'create-product',
      icon: <ShopOutlined style={{ color: '#22c55e' }} />,
      label: <span style={{ color: '#22c55e', fontWeight: 700 }}>Criar Produto</span>,
      path: `/add-product/${user._id}`,
      highlight: true
    };
    const commonItems = [
      {
        key: 'dashboard',
        icon: <HomeOutlined />,
        label: 'Dashboard',
        path: '/user/dashboard'
      },
      createProductBtn
    ];

    if (user.role === 'admin') {
      return [
        ...commonItems,
        {
          type: 'divider'
        },
        {
          key: 'admin-section',
          type: 'group',
          label: 'Administração',
          children: [
            {
              key: 'users',
              icon: <UserAddOutlined />,
              label: 'Gerenciar Usuários',
              path: '/admin/users'
            },
            {
              key: 'categories',
              icon: <TagOutlined />,
              label: 'Categorias',
              path: '/admin/add-category'
            },
            {
              key: 'locations',
              icon: <EnvironmentOutlined />,
              label: 'Localizações',
              path: '/admin/add-location'
            },
            {
              key: 'reports',
              icon: <ExclamationCircleOutlined />,
              label: 'Produtos Reportados',
              path: '/admin/reported-products'
            },
            {
              key: 'sponsored',
              icon: <CrownOutlined />,
              label: 'Anúncios Patrocinados',
              path: '/admin/sponsored-ads'
            }
          ]
        },
      ];
    } else {
      return commonItems;
    }
  };

  const handleMenuClick = (item) => {
    if (item.path) {
      history.push(item.path);
    }
  };

  const renderMenuItem = (item) => {
    if (item.type === 'divider') {
      return <div key={item.key} className="menu-divider" />;
    }
    
    if (item.type === 'group') {
      return (
        <div key={item.key} className="menu-group">
          <div className="menu-group-label">
            {item.label}
          </div>
          {item.children.map(child => renderMenuItem(child))}
        </div>
      );
    }

    const isActive = history.location.pathname === item.path;
    const isHighlight = item.highlight;
    return (
      <div
        key={item.key}
        onClick={() => handleMenuClick(item)}
        className={`nav-menu-item ${isActive ? 'active' : ''} ${isHighlight ? 'highlight' : ''}`}
      >
        <span className={`nav-menu-icon ${isActive ? 'active' : isHighlight ? 'highlight' : 'default'}`}>
          {item.icon}
        </span>
        <span>{item.label}</span>
      </div>
    );
  };

  return (
    <div className="unified-dashboard-outer">
      <Drawer
        title="Menu"
        placement="left"
        onClose={() => setDrawerVisible(false)}
        visible={drawerVisible}
        bodyStyle={{ padding: 0 }}
        style={{ maxHeight:'100vh' }}
        mask={isMobile} // Só mostrar mask no mobile
        closable={isMobile} // Só permitir fechar no mobile
        width={isMobile ? 280 : 250}
        getContainer={false} // Para não usar portal no desktop
      >
        <nav className="drawer-nav">
          {getMenuItems().map(item => renderMenuItem(item))}
        </nav>
      </Drawer>
      
      {/* Sidebar fixa no desktop */}
      {!isMobile && (
        <aside className="sidebar-fixed">
          <nav className="sidebar-nav">
            {getMenuItems().map(item => renderMenuItem(item))}
          </nav>
        </aside>
      )}

      <main
        className={`unified-main-content ${isMobile ? 'mobile' : 'desktop'}`}
      >
        {/* Header */}
        {isMobile ? 
        (<header className="unified-header">
          <div className="unified-header-info">
            <Button
              icon={<MenuOutlined />}
              className={`mobile-menu-btn ${isMobile ? 'mobile' : 'desktop'}`}
              onClick={() => setDrawerVisible(true)}
            />
            {user.role === 'admin' ? 'Painel de Administração' : 'Painel do Usuário'}
          </div>
        </header>) : <></>
      }
        
        {/* Content Area */}
        <section className="unified-content-section">
          <div className="unified-content-wrapper">{children}</div>
        </section>
      </main>
    </div>
  );
};

export default UnifiedDashboard;
